import { createContext, useContext } from "react";
import { parseInsitiUrl } from "../insiti-link";

export type SelectableRow = {
  key: string;
  type: string;
  id: number;
  userId: number;
};

interface TableSelection {
  rows: SelectableRow[];
  selectedKeys: Set<string>;
  toggle: (key: string) => void;
  toggleAll: () => void;
  selectableCount: number;
}

const Ctx = createContext<TableSelection | null>(null);
export const TableSelectionProvider = Ctx.Provider;

export function useTableSelection(): TableSelection | null {
  return useContext(Ctx);
}

interface HastNode {
  tagName?: string;
  properties?: { href?: string };
  children?: HastNode[];
}

function findFirstHref(node?: HastNode): string | undefined {
  if (!node) return undefined;
  if (node.tagName === "a" && node.properties?.href) return node.properties.href;
  for (const child of node.children ?? []) {
    const href = findFirstHref(child);
    if (href) return href;
  }
  return undefined;
}

function findFirst(node: HastNode, tag: string): HastNode | undefined {
  if (node.tagName === tag) return node;
  for (const child of node.children ?? []) {
    const found = findFirst(child, tag);
    if (found) return found;
  }
  return undefined;
}

function childrenWithTag(node: HastNode, tag: string): HastNode[] {
  return (node.children ?? []).filter((c) => c.tagName === tag);
}

// Identity of a single row, derived from the first insiti:// link it contains.
// Returns null unless the row carries a messageable profile (user_id present).
export function rowIdentity(node?: HastNode): SelectableRow | null {
  const parsed = parseInsitiUrl(findFirstHref(node));
  if (!parsed || parsed.user_id == null) return null;
  return {
    key: `${parsed.type}/${parsed.id}`,
    type: parsed.type,
    id: parsed.id,
    userId: parsed.user_id,
  };
}

// All selectable rows of a table, derived from the table's own hast node.
export function extractRowsFromNode(node?: HastNode): SelectableRow[] {
  if (!node) return [];
  const tbody = findFirst(node, "tbody") ?? node;
  const rows: SelectableRow[] = [];
  for (const tr of childrenWithTag(tbody, "tr")) {
    const identity = rowIdentity(tr);
    if (identity) rows.push(identity);
  }
  const seen = new Set<string>();
  return rows.filter((r) => (seen.has(r.key) ? false : (seen.add(r.key), true)));
}
